
import { api } from "@/api/api";
import {
  ApiCreateCourseParticipantCommentDto,
  ApiGetCourseDefaultOptionDto,
  ApiGetCourseParticipantCommentDto,
  ApiGetCustomerOrganizationAddressListDto,
  ApiGetGuestEmployeewithMemberOrganizationDto,
  ApiGetHierarchicalMemberOrganizationDto,
  ApiGetOrganizationFeatureSettingDto,
  ApiInt32IdDto,
  ApiUpsertCourseParticipantDto,
  ApiGetSectorDto,
} from "@/api/generated/Api";
import TheMemberOrganizaionModalTreeHierarchy from "@/components/administration/member-organizations/TheMemberOrganizaionModalTreeHierarchy.vue";
import CustomerOrganizationSearchInput from "@/components/common/CustomerOrganizationInput.vue";
import PersonSearchInput from "@/components/common/PersonSearchInput.vue";
import BaseModalForm from "@/components/shared/modal/BaseModalForm.vue";
import { DashboardUser } from "@/shared/constants/dashboardUser";
import { CourseParticipantStatus } from "@/shared/enums/CourseParticipantStatus.enum";
import { ParticipantRoles } from "@/shared/enums/ParticipantRoles.enum";
import { ModalType } from "@/shared/enums/modalTypeEnum";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { hasMemberOrgAccess, useRestrictedAccessApi } from "@/shared/helpers/accessLevelApiAdapter";
import { isVocationalSchool as useIsVocationalSchool } from "@/shared/helpers/curriculumHelpers";
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { openNotification } from "@/shared/helpers/store.helpers";
import { getValidatableRef } from "@/shared/helpers/typeHelpers";
import { required, validateMaxLength, validateNotEmpty } from "@/shared/helpers/validationHelpers";
import { useRoute, useStore } from "@/shared/useHelpers";
import { StoreState } from "@/store/store.state.interface";
import { PropType, Ref, computed, defineComponent, onMounted, ref } from "@vue/composition-api";
import {
  Blockquote,
  Bold,
  BulletList,
  Code,
  HardBreak,
  Heading,
  History,
  HorizontalRule,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Paragraph,
  Strike,
  TiptapVuetify,
  Underline, // @ts-ignore
} from "tiptap-vuetify";
import ChatLog from "./ChatLog.vue";
import { ActiveUser, MemberOrganization, Sectors } from "./ParticipantsTable.vue";
import { HierarchyLevel, checkMemberOrgLevel } from "@/shared/helpers/memberOrgHelpers";

// Returns a fresh default course participant object for initializing or resetting form state
const initialApplicant: () => ApiUpsertCourseParticipantDto = () => ({
  userId: 0,
  status: "",
  roleName: ParticipantRoles.Student,
  requiresElectronicInvoice: false,
  memberOrganizationId: null,
  sectorId: null,
});

// Returns a fresh default comment object for initializing or resetting comment form state
const initialComment: () => ApiCreateCourseParticipantCommentDto = () => ({
  comment: "",
  isGuestUserVisible: false,
});

/**
 * UpsertParticipantModal
 * - Modal for creating/updating a course participant
 * - Accepts initial context via props (course, user, org)
 * - Emits:
 *   - "updateParticipantsTable" after a successful upsert
 *   - "close" when the modal should be dismissed
 */
export default defineComponent({
  name: "UpsertParticipantModal",
  emits: ["updateParticipantsTable", "close"],
  components: {
    BaseModalForm,
    ChatLog,
    TiptapVuetify,
    PersonSearchInput,
    CustomerOrganizationSearchInput,
    TheMemberOrganizaionModalTreeHierarchy,
  },
  props: {
    headline: {
      type: String,
      required: true,
    },
    type: {
      type: String as PropType<ModalType>,
      required: true,
    },
    memberOrganizations: {
      type: Array as PropType<MemberOrganization[]>,
      required: true,
    },
    sectors: {
      type: Array as PropType<Sectors[]>,
      required: false,
    },
    activeUsers: {
      type: Array as PropType<ActiveUser[]>,
      required: true,
    },
    courseDefaultOptions: {
      type: Object as PropType<ApiGetCourseDefaultOptionDto>,
      required: true,
    },
    isCourseDone: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedUserId: {
      type: Number,
      required: true,
    },
    currentCourseId: {
      type: Number,
      required: true,
    },
    chosenMemberOrgId: {
      type: Number,
      required: true,
    },
    subOrganizerId: {
      type: Number,
      required: false,
    },
    chosenSectorId: {
      type: Number,
      required: false,
    },
    activeFeatureSettings: {
      type: Array as PropType<ApiGetOrganizationFeatureSettingDto[]>,
      required: true,
    },
  },
  setup(
    {
      type,
      courseDefaultOptions,
      currentCourseId,
      selectedUserId,
      chosenMemberOrgId,
      subOrganizerId,
      chosenSectorId,
      activeFeatureSettings,
    },
    { emit, refs }
  ) {
    const store = useStore<StoreState>();
    const route = useRoute();
    const restrictedAccessApi = useRestrictedAccessApi();
    const applicantData: Ref<ApiUpsertCourseParticipantDto> = ref(initialApplicant());
    const isVocationalSchool = computed(() => useIsVocationalSchool(store.state.plans.studyplan.mainCourseId));
    const isInvoiceAndPurchaserEqual = ref(true);
    const useParticipantAsInvoice = ref(true);
    const canEditName = ref(true);
    const participantComments = ref<ApiGetCourseParticipantCommentDto[]>([]);
    const createdComment = ref<ApiCreateCourseParticipantCommentDto>(initialComment());
    const currentUserId = ref(window.sessionStorage.getItem(DashboardUser.USER_ID) || 0);
    const editingGuestUserVisibility = ref(false);
    const guestUserVisibilityCollection = ref<ApiInt32IdDto[]>([]);
    const customerOrgSearchInput = ref<any>();
    let lastTime = 0;
    const organizations = ref<ApiGetCustomerOrganizationAddressListDto[]>([]);
    const QUERY_TEXT_MIN_LENGTH = 3;
    const isDisabled = ref(false);
    const entireMemberOrgHierarchi = ref<ApiGetHierarchicalMemberOrganizationDto[]>([]);
    const chosenMemberOrgs = ref<any[]>([]);
    const hierarchyLevel = ref<string>("");
    const courseParticipantStatuses = ref<string[]>([]);
    const hasSubOrganizer = ref<boolean>(false);
    const hasPreApprove = ref<boolean>(false);
    const currentGuestUser = ref<ApiGetGuestEmployeewithMemberOrganizationDto>();
    const isGuestUserSubOrganizer = ref<boolean>(false);
    const availableSectors = ref<ApiGetSectorDto[]>([]);
    const chosenSectors = ref<any[]>([]);

    // Fetch current guest user and check if they can approve participants
    const getCurrentUser = async () => {
      currentGuestUser.value = (await api.guestside.getGuestUserCurrentGuestEmployeeAsync()).data;
      await checkIfUserCanSetApprove();
    };

    // Check if guest user belongs to subOrganizer to grant approval rights
    const checkIfUserCanSetApprove = async () => {
      if (!currentGuestUser.value) {
        return;
      }

      if (currentGuestUser.value.memberOrganization?.id === subOrganizerId) {
        isGuestUserSubOrganizer.value = true;
      }
    };

    // Filter available participant statuses based on user role and hierarchy
    const filterStatuses = () => {
      const statuses: string[] = setParticipantStatuses() || [];

      // Skip filtering logic for non-guest users (only applies to guest user
      if (!hasMemberOrgAccess) {
        return;
      }

      // If user is a sub-organizer at any level, allow changing status to Applied, Approved, WaitingList, or Denied
      if (
        isGuestUserSubOrganizer.value &&
        (hierarchyLevel.value === HierarchyLevel.LOCAL_OFFICE ||
          hierarchyLevel.value === HierarchyLevel.REGIONAL_OFFICE ||
          hierarchyLevel.value === HierarchyLevel.MAIN_OFFICE)
      ) {
        courseParticipantStatuses.value = statuses.filter(
          (x) =>
            x === CourseParticipantStatus.Approved ||
            x === CourseParticipantStatus.Applied ||
            x === CourseParticipantStatus.WaitingList ||
            x === CourseParticipantStatus.Denied
        );
      }

      // At local office and NOT a sub-organizer, allow changing status to Applied, PreApproved, WaitingList, or Denied
      if (subOrganizerId && !isGuestUserSubOrganizer.value && hierarchyLevel.value === HierarchyLevel.LOCAL_OFFICE) {
        courseParticipantStatuses.value = statuses.filter(
          (x) =>
            x === CourseParticipantStatus.Applied ||
            x === CourseParticipantStatus.WaitingList ||
            x === CourseParticipantStatus.Denied ||
            x === CourseParticipantStatus.PreApproved
        );
      }

      // Without subOrganizer at local office, allow changing status to Applied, PreApproved, WaitingList, or Denied
      if (!subOrganizerId && hierarchyLevel.value === HierarchyLevel.LOCAL_OFFICE) {
        courseParticipantStatuses.value = statuses.filter(
          (x) =>
            x === CourseParticipantStatus.Applied ||
            x === CourseParticipantStatus.WaitingList ||
            x === CourseParticipantStatus.Denied ||
            x === CourseParticipantStatus.PreApproved
        );
      }

      // For courses without a sub-organizer, allow changing status to Applied, Approved, WaitingList, or Denied
      if (!subOrganizerId) {
        courseParticipantStatuses.value = statuses.filter(
          (x) =>
            x === CourseParticipantStatus.Applied ||
            x === CourseParticipantStatus.Approved ||
            x === CourseParticipantStatus.WaitingList ||
            x === CourseParticipantStatus.Denied
        );
      }
    };

    // Set feature flags (pre-approval, sub-organizer) based on the selected member organization
    const checkFeatureSettings = (id: number) => {
      if (!id) {
        return;
      }

      const memberOrganization = activeFeatureSettings.find((x) => x.organizationId === id);

      // Enable pre-approval if the organization uses it
      if (memberOrganization?.usePreApprovalForCourses) {
        hasPreApprove.value = true;
      }

      // Enable sub-organizer if the organization supports it or a subOrganizerId is present
      if (memberOrganization?.canUseSubOrganizer || subOrganizerId) {
        hasSubOrganizer.value = true;
      }
    };

    // Load the full organization hierarchy based on access level
    const getEntireMemberOrgHierarchi = async () => {
      // Is Guestuser
      if (hasMemberOrgAccess) {
        entireMemberOrgHierarchi.value = (await api.guestside.getHierarchyOfAllOrganizationsGuestUser()).data;
      }

      // Is not Guestuser / Is Internal user
      if (!hasMemberOrgAccess) {
        entireMemberOrgHierarchi.value = (await api.organization.getHierarchyOfAllOrganizations()).data;
      }
    };

    // Set the selected member organization and update related feature flags, hierarchy, and statuses
    const setMemberOrg = async (value: ApiGetHierarchicalMemberOrganizationDto) => {
      const chosenMemberOrg = {
        id: value.id || 0,
        name: value.name || "Fant ikke",
      };

      checkFeatureSettings(value.id);
      hierarchyLevel.value = (await checkMemberOrgLevel(value.id)) || HierarchyLevel.MAIN_OFFICE;
      filterStatuses();

      chosenMemberOrgs.value.push(chosenMemberOrg);
      applicantData.value.memberOrganizationId = value.id;
    };

    const getSectors = async () => {
      // Is Guestuser
      if (hasMemberOrgAccess) {
        availableSectors.value = (await api.sectors.getSectorsAsync()).data;
      }

      // Is not Guestuser / Is Internal user
      if (!hasMemberOrgAccess) {
        availableSectors.value = (await api.sectors.getSectorsAsync()).data;
      }
    };

    // Set the selected sector and bind it to the applicant payload
    const setSector = async (value: ApiGetSectorDto) => {
      const chosenSector = {
        id: value.id || 0,
        type: value.type || "Fant ikke sektor",
      };

      chosenSectors.value.push(chosenSector);
      applicantData.value.sectorId = value.id;
    };

    // Fetch organizations from API based on a query object
    const fetchOrganizations = async (query: any) => {
      if (!query) {
        return;
      }

      organizations.value = (await api.customer.getCustomerOrganizationAddressList(query)).data.items || [];

      if (!organizations.value.length) {
        openNotification(store, NotificationItemType.Warning, `Fant ingen organisasjoner på søket`);
      }
    };

    // Search for customer organizations with debounce and minimum query length
    const searchCustomerOrg = async (queryText: any) => {
      if (!queryText || queryText.length < QUERY_TEXT_MIN_LENGTH) {
        return;
      }

      const now = new Date().getTime();
      if (now - lastTime < 1000) {
        return;
      }
      lastTime = now;

      try {
        await fetchOrganizations({ Name: queryText });
      } catch (error) {
        openNotification(store, NotificationItemType.Error, "En feil oppsto ved søk etter kundeorganisasjon");
      }
    };

    // Toggle a comment ID in the guest-visibility collection (add if missing, remove if present)
    const updateGuestUserVisibilityCollection = (id: number): void => {
      const commentIds = guestUserVisibilityCollection.value.map((x: any) => x.id);

      if (commentIds.includes(id)) {
        guestUserVisibilityCollection.value.splice(commentIds.indexOf(id), 1);
        return;
      }

      guestUserVisibilityCollection.value.push({ id });
    };

    // Persist guest visibility changes for selected comments to the backend
    const changeGuestUserVisibility = () => {
      if (!guestUserVisibilityCollection.value.length) {
        return;
      }

      api.course.changeCourseParticipantsCommentGuestUserVisibilityAsync(
        currentCourseId,
        guestUserVisibilityCollection.value
      );
    };

    // Load participant comments based on access level
    const getParticitpantComments = async () => {
      if (!hasMemberOrgAccess) {
        participantComments.value = (
          await api.course.getCourseParticipantCommentsAsync(currentCourseId, selectedUserId)
        ).data.reverse();
      }

      if (hasMemberOrgAccess) {
        participantComments.value = (
          await api.guestside.getGuestUserCourseParticipantCommentsAsync(currentCourseId, selectedUserId)
        ).data.reverse();
      }
    };

    // Create a new comment for the participant based on access level
    const createComment = async () => {
      if (!createdComment.value.comment) {
        return;
      }

      if (hasMemberOrgAccess) {
        createdComment.value.isGuestUserVisible = true;
        await api.guestside.createGuestUserCourseParticipantCommentAsync(
          currentCourseId,
          applicantData.value.userId,
          createdComment.value
        );
      }

      if (!hasMemberOrgAccess) {
        await api.course.createCourseParticipantCommentAsync(
          currentCourseId,
          applicantData.value.userId,
          createdComment.value
        );
      }

      openNotification(store, NotificationItemType.Success, "Kommentar er lagt til");
    };

    // Initialize the selected member organization based on the chosenMemberOrgId prop
    const setInitMemberOrg = async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        if (chosenMemberOrgId) {
          let chosenMemberOrg;

          if (!hasMemberOrgAccess) {
            chosenMemberOrg = (await api.organization.getMemberOrganizationAsync(chosenMemberOrgId)).data;
          }

          if (hasMemberOrgAccess) {
            const response = (await api.guestside.getGuestUserMemberOrganizations()).data;
            chosenMemberOrg = response.find((x) => x.id === chosenMemberOrgId);
          }

          if (chosenMemberOrg) {
            setMemberOrg(chosenMemberOrg);
          }
        }
      });
    };

    // Initialize the selected sector based on the chosenSectorId prop
    const setInitSector = async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        if (chosenSectorId) {
          let response;

          // Use the correct endpoint based on user access level
          if (hasMemberOrgAccess) {
            response = (await api.sectors.getSectorsAsync()).data;
          } else {
            response = (await api.sectors.getSectorsAsync()).data;
          }

          const chosenSector = response.find((x) => x.id === chosenSectorId);

          if (chosenSector) {
            setSector(chosenSector);
          }
        }
      });
    };

    // Return participant statuses based on user type and course type
    const setParticipantStatuses = () => {
      if (hasMemberOrgAccess) {
        return courseDefaultOptions.memberOrganizationParticipantStatuses;
      }
      if (isVocationalSchool.value) {
        return courseDefaultOptions.vocationalSchoolParticipantStatuses;
      }
      return courseDefaultOptions.participantStatuses;
    };

    // Format active user display text
    const getActiveUserText = (item: ActiveUser) =>
      `${item.firstName} ${item.lastName} - ${item.email} - ${item.birthDate}`;

    onMounted(() => {
      globalLoadingWrapper({ blocking: true }, async () => {
        courseParticipantStatuses.value = setParticipantStatuses() || [];
        getEntireMemberOrgHierarchi();
        await getSectors();

        if (hasMemberOrgAccess) {
          await getCurrentUser();
        }

        if (type === ModalType.Add) {
          return;
        }

        if (type === ModalType.Edit) {
          canEditName.value = false;
        }

        await setInitMemberOrg();
        await setInitSector();

        getParticitpantComments();

        // Read initial participant data from the store
        const {
          userId,
          status,
          roleName,
          requiresElectronicInvoice,
          memberOrganizationId,
          sectorId,
          invoiceRecipient,
          purchaser,
        } = store.state.courseParticipants.person;

        // For guest users, restrict editing unless status is Applied or PreApproved
        if (hasMemberOrgAccess) {
          if (status !== CourseParticipantStatus.Applied && status !== CourseParticipantStatus.PreApproved) {
            isDisabled.value = true;
          }

          // Sub-organizers can edit unless status is already Approved
          if (isGuestUserSubOrganizer.value && status !== CourseParticipantStatus.Approved) {
            isDisabled.value = false;
          }
        }

        // Populate local form state from store values
        applicantData.value = {
          userId,
          status,
          roleName,
          requiresElectronicInvoice,
          memberOrganizationId: memberOrganizationId || null,
          sectorId: sectorId || null,
          purchaserId: purchaser?.customerId || null,
          invoiceRecipientId: invoiceRecipient?.customerId || null,
        };

        // Determine initial state for invoice/purchaser fields
        if (applicantData.value.purchaserId !== applicantData.value.invoiceRecipientId) {
          isInvoiceAndPurchaserEqual.value = false;
        }

        // If an invoice recipient is already set, disable using participant as invoice
        if (applicantData.value.invoiceRecipientId && applicantData.value.invoiceRecipientId > 0) {
          useParticipantAsInvoice.value = false;
        }
      });
    });

    // Handle form submission for creating/updating a participant
    const onSubmit = async () => {
      const isValid = getValidatableRef(refs.form)?.validate();
      if (!isValid) {
        return;
      }

      globalLoadingWrapper({ blocking: true }, async () => {
        createComment();
        changeGuestUserVisibility();

        // Sync purchaser and invoice recipient if marked as equal
        if (isInvoiceAndPurchaserEqual.value) {
          applicantData.value.invoiceRecipientId = applicantData.value.purchaserId;
        }

        // Call API to upsert the participant
        await restrictedAccessApi.upsertCourseParticipant(
          +route.params.id,
          {
            ...applicantData.value,
            purchaserId: useParticipantAsInvoice.value ? null : applicantData.value.purchaserId,
            invoiceRecipientId: useParticipantAsInvoice.value ? null : applicantData.value.invoiceRecipientId,
          },
          applicantData.value.userId
        );

        openNotification(
          store,
          NotificationItemType.Success,
          type === ModalType.Edit ? "Bruker oppdatert" : "Bruker lagt til"
        );

        store.commit("courseParticipants/SET_PERSON", {
          userId: applicantData.value.userId,
          status: applicantData.value.status,
          roleName: applicantData.value.roleName,
          requiresElectronicInvoice: applicantData.value.requiresElectronicInvoice,
          memberOrganizationId: applicantData.value.memberOrganizationId,
          sectorId: applicantData.value.sectorId,
          purchaser: { customerId: applicantData.value.purchaserId },
          invoiceRecipient: { customerId: applicantData.value.invoiceRecipientId },
        });

        emit("updateParticipantsTable");
        emit("close");
      });
    };

    return {
      ModalType,
      applicantData,
      validateNotEmpty,
      validateMaxLength,
      chosenMemberOrgs,
      entireMemberOrgHierarchi,
      required,
      onSubmit,
      isVocationalSchool,
      hasMemberOrgAccess,
      courseParticipantStatuses,
      getActiveUserText,
      isInvoiceAndPurchaserEqual,
      useParticipantAsInvoice,
      canEditName,
      createdComment,
      participantComments,
      currentUserId,
      changeGuestUserVisibility,
      editingGuestUserVisibility,
      guestUserVisibilityCollection,
      updateGuestUserVisibilityCollection,
      updatePurchaserId: (orgId: number) => (applicantData.value.purchaserId = orgId),
      updateInvoiceRecipientId: (orgId: number) => (applicantData.value.invoiceRecipientId = orgId),
      customerOrgSearchInput,
      searchCustomerOrg,
      organizations,
      minLength: QUERY_TEXT_MIN_LENGTH,
      isDisabled,
      setMemberOrg,
      availableSectors,
      chosenSectors,
      setSector,
    };
  },

  data: () => ({
    extensions: [
      History,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      Bold,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak,
    ],
  }),
});
